import {Component, OnDestroy, OnInit} from '@angular/core';
import {Product} from "../product";
import {ActivatedRoute, Router} from "@angular/router";
import {Store} from "@ngrx/store";
import * as fromStore from '../../../store';
import {getTenWebBuilderPlugin} from '../../../store';
import {ActivityAction, ActivityStatus, IActivity} from "../../../activities/activity";
import {Website} from "../website";
import {AiBuilderCreatePageComponent} from "./dialogs/ai-builder-create-page/ai-builder-create-page.component";
import {DbModalService} from "../../../modules/modal/modal";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {Observable} from "rxjs";
import {Subject} from "rxjs";
import {User} from "../../../user/user";
import {UserService} from "../../../user/user.service";
import {combineLatest} from "rxjs";
import {TypeUtils} from "../../../shared/utils/type-utils";
import {TextUtils} from "../../../shared/utils/text-utils";
import {Config} from "../../../config/config";
import {
  NotificationDialogComponent
} from "../../../shared/components/dialogs/notification-dialog/notification-dialog.component";
import {DialogService} from "../../../../ng2-bootstrap-modal/dialog.service";
import {DashboardUtils} from "../../../shared/utils/dashboard-utils";
import {UserSubscription} from "../../../user/user-subscription";
import {ProductService} from "../product.service";
import {
  CreateAiAssistantComponent
} from "../../../components/pages/setup/create-ai/create-ai-assistant/create-ai-assistant.component";
import {
  NotificationModalComponent
} from "../../../shared/components/dialogs/notification-modal/notification-modal.component";
import {GoogleAnalyticsService} from "../../../shared/services/google-analytics/google-analytics.service";
import {Utils} from "../../../shared/utils/utils";
import {BuilderV2Component} from "../../../builder-v2/builder-v2.component";
import {BuilderHistoryService} from "../../../builder-v2/builder-history.service";
import {CookieService} from "ngx-cookie";
import {
  SuccessMobileDialogComponent
} from "../../../builder-v2/components/success-mobile-dialog/success-mobile-dialog.component";
import {BuilderSectionsInteractionService} from "../../../builder-v2/builder-sections-interaction.service";
import {AiBuilderCommonService} from "./ai-builder-common.service";
import {Location} from "@angular/common";
import {
  OneWeekFreeDemoUpgradeComponent
} from "../../../shared/components/dialogs/one-month-free/components/one-week-free-demo-upgrade/one-week-free-demo-upgrade.component";

@Component({
    selector: 'db-ai-builder',
    templateUrl: './ai-builder.component.html',
    styleUrls: ['./ai-builder.component.scss'],
    standalone: false
})
export class AiBuilderComponent implements OnInit, OnDestroy {

  public user: User;
  public userSubscription: UserSubscription;
  public builderPlugin: Product;
  public elementorPlugin: Product;
  public tenWebTheme: Product;
  public website: Website;
  public loading: boolean = true;
  public userLocalStorage: any;
  public seenNewAiFlow: boolean;
  public onboardingPassed: boolean;
  public onboardingFinished: boolean;
  public hasFrontPage: boolean;
  public pagesLimit: number = 10;
  public showFailedDialog: boolean;
  public closedDialog: boolean = false;
  public demoUser: boolean = false;
  public canGenerateWebsites: boolean = true;

  public ongoingActivity$: Observable<IActivity>;
  public ongoingBuilderActivity$: Observable<IActivity>;
  successPopupSeen: boolean = false;
  isMobile = false;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _store: Store<fromStore.DbStoreState>,
    private _dialogService: DialogService,
    private _modalService: DbModalService,
    private _productService: ProductService,
    private _userService: UserService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private builderHistoryService: BuilderHistoryService,
    private _cookieService: CookieService,
    private builderInteractionsService: BuilderSectionsInteractionService,
    public aiBuilderService: AiBuilderCommonService,
    private _location: Location
  ) {
  }

  ngOnInit() {
    const queryParams: any = (<any>this._route.queryParams).value;
    if (queryParams?.isNavigating) {
        setTimeout(() => {
          this.aiBuilderService.pageLoading.next(false);
          const queryParamsIndex: number = this._router.url.indexOf('?');
          if (queryParamsIndex !== -1) {
            this._location.replaceState(this._router.url.substring(0, queryParamsIndex));
          }
        }, 5000);
    } else {
      this.aiBuilderService.pageLoading.next(false);
    }

    this.isMobile = window.innerWidth <= 991;
    this.website = this._route.snapshot.parent.data['website'];
    this.elementorPlugin = this._route.snapshot.data['elementorPlugin'];
    this.builderPlugin = this._route.snapshot.data['builderPlugin'];

    const showSuccess = this._route.snapshot.queryParams['showSuccess'];
    if (showSuccess) {
      this._successDialog();
    }
    this._addEventHandlers();
    this._getBuilderOngoingActivity();
  }

  public ngOnDestroy(): void {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _addEventHandlers(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserLocalStorage),
          this._store.select(fromStore.getUserSubscription),
          this._store.select(fromStore.getCreatedPages(this.website.id)),
          this._store.select(fromStore.getRegeneratePermission)
        )),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([user, userLocalStorage, userSubscription, pages, canRegenerate]) => {
        this.user = user;
        this.userLocalStorage = userLocalStorage;
        this.userSubscription = userSubscription;
        this.canGenerateWebsites = canRegenerate;
        this.seenNewAiFlow = userLocalStorage && (TypeUtils.isDefined(userLocalStorage['seenNewAiFlow' + this.website.id]) || TypeUtils.isDefined(userLocalStorage['seenNewAiFlow']));
        this.onboardingPassed = userLocalStorage && (TypeUtils.isDefined(userLocalStorage['onboardingPassed' + this.website.id]) || TypeUtils.isDefined(userLocalStorage['onboardingPassed']));
        this.onboardingFinished = userLocalStorage && TypeUtils.isDefined(userLocalStorage['onboardingFinished']) ? userLocalStorage['onboardingFinished'] : false;
        this.demoUser = Utils.isDemoUser(userSubscription, user);
        if (pages && TypeUtils.isDefined(pages) && !TypeUtils.isEmpty(pages) && !this.onboardingPassed && !this.onboardingFinished) {
          this.onboardingPassed = true;
          this.onboardingFinished = true;
          this._setOnBoardingPassed();
        }
      });
    !this.builderPlugin && this._getBuilderPlugin();
    this._getOngoingBuilderState();
    this._getTenWebTheme();
    this._getOngoingElementorState();
    this._getProductOngoingActivity();
  }

  private _setOnBoardingPassed(): void {
    this.userLocalStorage['onboardingFinished'] = true;
    this.userLocalStorage['onboardingPassed' + this.website.id] = true;
    this.userLocalStorage['setupAiRecreation'] = false;
    this._userService.setToLocal(this.userLocalStorage);
  }

  /*private getUserLocalStorage(): void {
    this._store.select(getUserLocalStorageLoaded)
      .pipe(
        first(loaded => loaded),
        switchMap(() => this._store.select(getUserLocalStorage)),
        first(),
      )
      .subscribe(userLocalStorage => {
        this._userLocalStorage = userLocalStorage;
      });
  }*/

  public installAndActivate(): void {
    this.loading = true;
    let network = 0;
    if (this.website.domain_hash) {
      network = 1;
    }
    this._store.dispatch(new fromStore.InitWebsiteProductAction({
      website: this.website,
      products: [this.elementorPlugin, this.builderPlugin],
      action: ActivityAction.INSTALL_ACTIVATE,
      network: network
    }));
  }

  public createPageDialog(data): void {
    const modal = this._modalService.open(AiBuilderCreatePageComponent, {
      height: 600,
      width: 940,
      whiteCloseBtn: (window.innerWidth > 991)
    });
    modal.componentInstance.website = this.website;
    modal.componentInstance.trialUser = this.user.starterClient;
    modal.componentInstance.userEmail = this.user.email;
    modal.componentInstance.kitData = data.kitData;
    modal.componentInstance.menuTermId = data.menuTermId;
    modal.componentInstance.menuItemPosition = data.menuItemPosition;
    modal.componentInstance.menuItemId = data.menuItemId;
    modal.componentInstance.hasFrontPage = data.hasFrontPage;
    modal.componentInstance.builderPlugin = this.builderPlugin;
    modal.componentInstance.elementorPlugin = this.elementorPlugin;
    modal.componentInstance.demoUser = this.demoUser;
    modal.closed.subscribe((value) => {
      value && value.showUpgrade === 'openUpgrade' && this.openCheckoutDialog(value.from);
    });
    this.hasFrontPage = data.hasFrontPage;
  }

  private openCheckoutDialog(from): void {
    const analyticsData = {
      eventCategory: 'Free Upgrade Offer',
      eventAction: 'Locked button click',
      eventLabel: `AI Builder: Add Page: ${from}`
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    const firstTime = TypeUtils.isNull(localStorage.getItem('dbShowFirstTime'));
    const options = {
      overflowHidden: false,
    };
    if (!firstTime) {
      options['width'] = 1240;
    } else {
      options['width'] = 460;
      options['height'] = 600;
    }
    const modal = this._modalService.open(OneWeekFreeDemoUpgradeComponent, options);
    modal.componentInstance.showFirstTime = firstTime;
  }

  public regenerateDialog($event?): void {
    this.closedDialog = false;
    localStorage.removeItem('showFinalize');
    if ($event && $event.isRegenerateFromDemo) {
      const storageData = this.builderHistoryService.getStateFromStorage();
      if ($event.currentOutline && storageData) {
        const builderV2 = this._modalService.open(BuilderV2Component, {
          fullHeight: true,
          hasCloseBtn: false,
        });
        builderV2.componentInstance.domainId = this.website.id;
        builderV2.componentInstance.user = this.user;
        builderV2.componentInstance.popupMode = true;
        builderV2.componentInstance.currentOutline = true;
        builderV2.componentInstance.demoUser = this.demoUser;
        return;
      } else {
        return this._createAiAssistantDialog();
      }
    }
    const confirmationModal = this._modalService.open(NotificationModalComponent, {width: 700});
    confirmationModal.componentInstance.header = TextUtils.getText('text_regenerateTheEntireWebsite');
    confirmationModal.componentInstance.buttonAdditionalClass = 'ds-btn-black small-radius';
    confirmationModal.componentInstance.leftAlight = true;
    if (this.tenWebTheme && this.tenWebTheme.active) {
      confirmationModal.componentInstance.discardButtonTxt = 'Cancel';
      confirmationModal.componentInstance.discardButtonClass = 'ds-btn-outline-eggshell ds-btn-cancel m small-radius';
      confirmationModal.componentInstance.buttonAdditionalClass = 'ds-btn-black ds-btn-big m small-radius';
      confirmationModal.componentInstance.hideCloseBtn = 'true';
      confirmationModal.componentInstance.buttonTxt = 'Yes, regenerate my website';
      confirmationModal.componentInstance.uppercaseWords = false;
      confirmationModal.componentInstance.message = 'Generate a brand-new website using AI.<br/><br/>Your homepage will be replaced with the new AI-generated version.<br/>Your existing pages will remain untouched.';
    } else {
      confirmationModal.componentInstance.buttonTxt = TextUtils.getText('text_installActivate');
      confirmationModal.componentInstance.hideDialogDelay = 10;
      confirmationModal.componentInstance.buttonAdditionalClass = 'ds-btn-l';
      confirmationModal.componentInstance.message =
        `${TextUtils.getText('text_websiteGenerationWithAiIsNotAvailableWithYourCurrentTheme')}<br/> your current theme. ` +
        `<span>${TextUtils.getText('text_installActivateAniiBuilderThemeToGetAccess', {companyName: '10web'})}</span> <br/>` +
        `<p class="mt20">${TextUtils.getText('text_theNewAiGeneratedPageWillBecomeYourSitesHomepage')}</p>`;
    }
    confirmationModal.closed.subscribe((value) => {
      if (value) {
        if (this.tenWebTheme && this.tenWebTheme.active) {
          this._createAiAssistantDialog();
        } else {
          this._installAndActivate();
          this._createAiAssistantDialog();
        }
      }
    });
  }


  private _ongoingActivities(): void {
    this._store.select(fromStore.getOngoingActivities)
      .subscribe((data): void => {
        if (data) {
          const ongoingActivitiesDone = data.filter((activity): any =>
            activity && activity.meta && activity.meta.product_data
            && activity.meta.product_data.slug && activity.meta.product_data.slug === Config.tenWebThemeSlug
            && activity.status === ActivityStatus.DONE
          );
          if (ongoingActivitiesDone.length && this.closedDialog === true) {
            this.closedDialog = false;
            this._createAiAssistantDialog();
          }
        }
      });
  }

  private _createAiAssistantDialog(): void {
    const modal = this._modalService.open(CreateAiAssistantComponent, {fullHeight: true});
    modal.componentInstance.websiteId = this.website.id;
    modal.componentInstance.isOnboarding = false;
    modal.componentInstance.isFromGenerate = true;
    modal.componentInstance.showAsDialog = true;
    modal.componentInstance.popupMode = true;
    this.successPopupSeen = false;
  }

  private _installAndActivate(): void {
    this._store.dispatch(new fromStore.InitWebsiteProductAction({
      website: this.website,
      products: [this.tenWebTheme],
      action: ActivityAction.INSTALL_ACTIVATE,
      network: 1
    }));
  }

  private _getBuilderPlugin(): void {
    this._store.select(getTenWebBuilderPlugin)
      .pipe(first())
      .subscribe((builderProduct: Product) => {
        if (builderProduct) {
          this.builderPlugin = builderProduct;
          this._getProductOngoingActivity();
        }
      });
  }

  private _getOngoingBuilderState(): void {
    this._store.select(fromStore.getWebsiteBuilderPlugin(this.website.id))
      .subscribe((builderPlugin) => {
        if (builderPlugin) {
          this.builderPlugin = builderPlugin;
        }
      });
  }

  private _getOngoingElementorState(): void {
    this._store.select(fromStore.getWebsiteElementorPlugin(this.website.id))
      .subscribe((elementorPlugin) => {
        if (elementorPlugin) {
          this.elementorPlugin = elementorPlugin;
          this.loading = false;
        }
      });
  }

  private _getTenWebTheme(): void {
    this._store.select(fromStore.getWebsiteTenWebTheme(this.website.id))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(theme => this.tenWebTheme = theme);
  }

  private _getProductOngoingActivity(): void {
    this.ongoingActivity$ = this._store.select(fromStore.getProductOngoingActivity(this.website.id, this.builderPlugin.slug));
    this.ongoingActivity$
      .pipe(
        takeUntil(this._destroyStream$),
        filter(activity => !!activity)
      )
      .subscribe((activity) => {
        this.loading = !(activity.status === 'done' || activity.status === 'failed');
      });
  }

  private _getBuilderOngoingActivity(): void {
    this.ongoingBuilderActivity$ = this._store.select(
      fromStore.getBuilderOngoingActivity(this.website.id, Config.builderPluginSlug, !!this.website.domain_hash)
    );
    this.ongoingBuilderActivity$
      .pipe(
        takeUntil(this._destroyStream$),
        filter(activity => !!activity)
      )
      .subscribe(activity => {
        if (activity.status === 'done' && !this.successPopupSeen) {
          this._modalService.dismissAll();
          this._dialogService.hasOpenedDialog() && this._dialogService.removeAll();
          this._showCongratsDialog(activity);
        } else if (activity.status === 'failed' && !this.showFailedDialog) {
          this.showFailedDialog = true;
          this._modalService.dismissAll();
          this._dialogService.hasOpenedDialog() && this._dialogService.removeAll();
          this._showFailedDialog();
        }
      });
  }

  private _showCongratsDialog(activity): void {
    let pageUrl;
    const dbTesting = !!this._cookieService.get('db-testing');
    if (this.demoUser) {
      pageUrl = activity && activity.meta && activity.meta.page_link + `?from=regenerate&domain_id=${Config.demoDomainId}&client_id=${this.user?.id}&first_time&client_email=${this.user?.email}` + (dbTesting ? `&db-testing` : ``);
    } else {
      if (activity?.meta?.page_id) {
        if (this.userSubscription?.trialClient) {
          pageUrl = `${this.website.site_url}/?page_id=${activity.meta.page_id}`;
        } else {
          pageUrl = DashboardUtils.generateWpAdminRedirectorUrl(
            this.website.id,
            `${this.website.adminUrl}/post.php?post=${activity.meta.page_id}&action=elementor`
          );
        }
      } else {
        if (this.userSubscription?.trialClient) {
          pageUrl = this.website.site_url;
        } else {
          pageUrl = DashboardUtils.generateWpAdminRedirectorUrl(
            this.website.id,
            `${this.website.adminUrl}?edit.php?post_type=page&from=tenweb_dashboard&open=homepage`
          );
        }
      }
    }
    this._successDialog(pageUrl);
  }

  private _successDialog(pageUrl?): void {
    this._store.dispatch(new fromStore.LoadBuilderCreatedPages(this.website.id));
    this._store.dispatch(new fromStore.LoadGeneratedWebsiteCount());
    this.successPopupSeen = true;
    if (this.isMobile) {
      const modal = this._modalService.open(SuccessMobileDialogComponent, {});
      modal.componentInstance.header = 'Congrats!';
      modal.componentInstance.message = 'Add details about your brand or experience?';
      modal.componentInstance.buttonText = 'Preview & edit';
      modal.closed.subscribe(() => {
        const websiteUrl = this.website?.adminUrl + '?edit.php?post_type=page&from=tenweb_dashboard&open=homepage';
        window.open(DashboardUtils.generateWpAdminRedirectorUrl(this.website?.id, websiteUrl, this.user?.email), "_blank", "noopener");
        setTimeout(() => {
          this.builderHistoryService.clearFormData();
        }, 500);
      });
    } else {
      this._dialogService.addDialog(NotificationDialogComponent, {
        header: TextUtils.getText('text_congratsFromManager'),
        message: TextUtils.getText('text_websiteSuccessfullyGeneratedWithAI'),
        buttonText: TextUtils.getText('text_previewAndEdit'),
        buttonColor: 'black',
        hasGradientBorder: true,
        action: 'install_hosted_template',
        hasAdminLink: false,
        websiteId: this.website.id,
        hasCloseButton: false
      })
        .subscribe((confirmed: string): void => {
          if (confirmed === 'install_hosted_template') {
            const websiteUrl = this.website?.adminUrl + '?edit.php?post_type=page&from=tenweb_dashboard&open=homepage';
            window.open(DashboardUtils.generateWpAdminRedirectorUrl(this.website?.id, websiteUrl, this.user?.email), "_blank", "noopener");
          }
        });
    }
  }

  private _showFailedDialog(): void {
    this._dialogService.addDialog(NotificationDialogComponent, {
      header: 'Something went wrong :/',
      message: 'We encountered an error while generating your website. Contact our customer care team to get it fixed ASAP.',
      buttonText: 'Got It',
      buttonColor: 'blue',
      action: 'failed_import_template',
      hasCloseButton: false
    })
      .subscribe((confirmed: string): void => {
        if (confirmed === 'failed_import_template') {
          this.openChat();
        }
      });
  }

  private openChat() {
    window['Smooch']?.open();
    this._store.dispatch(new fromStore.ToggleChatOverlay(false));
  }
}
